/**
 * /admin — the admin landing.
 *
 * Lists ONLY the admin functions the server reports for this caller
 * (GET /capabilities → admin.functions). Holding one function never shows,
 * or implies, another; each screen is guarded on its own function and every
 * route is enforced by the server independently.
 */

import { Link } from 'react-router-dom';
import { useGetCapabilitiesQuery } from '../../store/api/capabilitiesApi';
import { adminFunctionsFrom } from '../../components/routing/adminAccess';
import type { AdminFunction } from '../../store/api/capabilitiesApi';

export function AdminFunctionList({ functions }: { functions: AdminFunction[] }) {
  if (functions.length === 0) {
    return <p className="text-sm text-gray-500" data-testid="admin-no-functions">You have no admin functions.</p>;
  }
  return (
    <ul className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="admin-functions">
      {functions.map((fn) => (
        <li key={fn.key} className="rounded-lg border border-gray-200 bg-white p-4" data-testid={`admin-function-${fn.key}`}>
          <Link to={fn.path} className="font-semibold text-primary-600 hover:text-primary-700">
            {fn.label}
          </Link>
          <p className="mt-1 text-sm text-gray-500">{fn.description}</p>
        </li>
      ))}
    </ul>
  );
}

export function AdminLandingPage() {
  const { data } = useGetCapabilitiesQuery();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
        <p className="mt-1 text-gray-500">The admin functions available to you.</p>
      </div>
      <AdminFunctionList functions={adminFunctionsFrom(data)} />
    </div>
  );
}
